import {
  applyPatches,
  enablePatches,
  produce,
  produceWithPatches,
  type Draft,
  type Patch,
} from 'immer';
import { validateDocument, type ValidationIssue, type ValidationResult } from '../validation.js';
import type { ProjectDocument } from '../types.js';
import { shellViolation } from '../shell.js';

enablePatches();

export type { Patch } from 'immer';

/**
 * Applies patches recorded by someone else (another participant, or this client before a
 * rebase). Throws when a path no longer resolves, e.g. the entity was deleted meanwhile.
 */
export function applyDocumentPatches(
  doc: ProjectDocument,
  patches: readonly Patch[],
): ProjectDocument {
  return applyPatches(doc, patches as Patch[]);
}

/** Like `applyDocumentPatches`, but also refuses results that break the document rules. */
export function applyValidatedPatches(
  doc: ProjectDocument,
  patches: readonly Patch[],
  validate: Validator = validateDocument,
): { ok: true; document: ProjectDocument } | { ok: false; reason: string } {
  let next: ProjectDocument;
  try {
    next = applyDocumentPatches(doc, patches);
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : 'patch' };
  }
  const validation = validate(next);
  if (!validation.ok) return { ok: false, reason: validation.issues[0]?.message ?? 'invalid' };
  return { ok: true, document: next };
}

/**
 * A command mutates an Immer draft. Recording patches instead of hand-written inverse
 * operations guarantees that undo restores exactly the previous state.
 */
export interface Command {
  readonly label: string;
  apply(draft: Draft<ProjectDocument>): void;
}

export interface HistoryEntry {
  readonly label: string;
  readonly patches: readonly Patch[];
  readonly inversePatches: readonly Patch[];
}

export interface HistoryState {
  readonly past: readonly HistoryEntry[];
  readonly future: readonly HistoryEntry[];
  readonly limit: number;
}

export const DEFAULT_HISTORY_LIMIT = 100;

export function createHistory(limit: number = DEFAULT_HISTORY_LIMIT): HistoryState {
  return { past: [], future: [], limit };
}

export type ExecuteResult =
  | {
      readonly ok: true;
      readonly document: ProjectDocument;
      readonly history: HistoryState;
      readonly changed: boolean;
    }
  | { readonly ok: false; readonly issues: readonly ValidationIssue[] };

export type Validator = (doc: unknown) => ValidationResult;

/**
 * Expected, user-facing refusal of a command (e.g. "no space left in this wall"). Reported
 * like a validation issue instead of crashing; any other exception is a programming error.
 */
export class CommandError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CommandError';
  }
}

/**
 * Applies a command for a transient preview (e.g. while dragging in 3D): no history and no
 * full validation, which would be too slow per pointer move. Refusals return null. The final
 * state must still go through `executeCommand`.
 */
export function previewCommand(doc: ProjectDocument, command: Command): ProjectDocument | null {
  try {
    const next = produce(doc, (draft) => {
      command.apply(draft);
    });
    return shellViolation(doc, next) ? null : next;
  } catch {
    // A preview must never break the interaction; the final command reports the reason.
    return null;
  }
}

export function executeCommand(
  document: ProjectDocument,
  history: HistoryState,
  command: Command,
  validate: Validator = validateDocument,
): ExecuteResult {
  let produced: readonly [ProjectDocument, Patch[], Patch[]];
  try {
    produced = produceWithPatches(document, (draft) => {
      command.apply(draft);
    });
  } catch (err) {
    if (err instanceof CommandError)
      return { ok: false, issues: [{ path: '', message: err.message }] };
    throw err;
  }
  const [next, patches, inversePatches] = produced;
  if (patches.length === 0) return { ok: true, document, history, changed: false };
  const shell = shellViolation(document, next);
  if (shell) return { ok: false, issues: [{ path: '', message: shell }] };

  const validation = validate(next);
  if (!validation.ok) return { ok: false, issues: validation.issues };

  const past = [...history.past, { label: command.label, patches, inversePatches }];
  const trimmed = past.length > history.limit ? past.slice(past.length - history.limit) : past;
  return {
    ok: true,
    document: next,
    history: { ...history, past: trimmed, future: [] },
    changed: true,
  };
}

export interface HistoryStep {
  readonly document: ProjectDocument;
  readonly history: HistoryState;
  readonly label: string;
}

export function undo(document: ProjectDocument, history: HistoryState): HistoryStep | null {
  const entry = history.past.at(-1);
  if (!entry) return null;
  return {
    document: applyPatches(document, entry.inversePatches as Patch[]),
    history: { ...history, past: history.past.slice(0, -1), future: [entry, ...history.future] },
    label: entry.label,
  };
}

export function redo(document: ProjectDocument, history: HistoryState): HistoryStep | null {
  const entry = history.future[0];
  if (!entry) return null;
  return {
    document: applyPatches(document, entry.patches as Patch[]),
    history: { ...history, past: [...history.past, entry], future: history.future.slice(1) },
    label: entry.label,
  };
}

/**
 * Joins all steps recorded after `since` (the newest entry before they began, or null for an
 * empty history) into one undo step, e.g. the many small turns of a held rotate button.
 */
export function mergeRecent(history: HistoryState, since: HistoryEntry | null): HistoryState {
  const start = since ? history.past.lastIndexOf(since) + 1 : 0;
  // `since` may have been trimmed off the history meanwhile: then nothing is merged.
  if (since && start === 0) return history;
  const steps = history.past.slice(start);
  const last = steps.at(-1);
  if (!last || steps.length < 2) return history;
  const merged: HistoryEntry = {
    label: last.label,
    patches: steps.flatMap((s) => s.patches),
    inversePatches: [...steps].reverse().flatMap((s) => s.inversePatches),
  };
  return { ...history, past: [...history.past.slice(0, start), merged] };
}

/** Groups several commands into one undo step, e.g. "create hall" = building + 4 walls. */
export function composite(label: string, commands: readonly Command[]): Command {
  return {
    label,
    apply(draft) {
      for (const c of commands) c.apply(draft);
    },
  };
}
